/**
 * Public site header — neo-brutalist top bar.
 *
 * Shared by every public route through PublicLayout, so it is the one place
 * that guarantees a consistent way back to the homepage and into the
 * dashboard. The auth control is deliberately the LAST item on the right at
 * every breakpoint: logged out it reads "Đăng nhập", logged in it becomes the
 * user chip with the dashboard/logout menu.
 *
 * [2026-08-31] BỎ mục "Lịch thi": trang chủ đã có sẵn băng lịch sát hạch
 * (`#lich-thi`) kèm nút sang danh sách từng ngày, nên treo thêm một mục trên
 * thanh header chỉ là kể lại lần hai. Hai trang từng nằm trong menu đó vẫn vào
 * được từ chân trang (khối "Dành cho học viên"), còn danh sách dự thi thì trang
 * chủ dẫn thẳng tới từ băng lịch và từ nút ở khối mở đầu.
 *
 * [2026-08-31] KHÔNG CÒN MENU THẢ XUỐNG. Mọi mục trên thanh nay là LINK PHẲNG:
 * bấm nhãn là ra thẳng trang đích. Từng mục có menu con đã lần lượt bỏ đi vì
 * chính trang đích đã bày đủ lối vào — "Bảng tin" có bộ lọc chuyên mục ngay đầu
 * trang, "Ôn tập & thi thử" có ba thẻ, "Công cụ" có lưới đủ mọi công cụ — nên
 * tấm thả xuống chỉ bắt người dùng chọn hai lần cho cùng một việc. Ngăn kéo
 * điện thoại vì thế cũng chỉ còn một hàng bấm-là-đi cho mỗi mục.
 *
 * @module layouts/Header
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../features/auth/hooks/useAuth';
import { getDefaultAvatar } from '../shared/utils/avatarUtils';
import './Header.scss';

type NavItem = {
  key: string;
  label: string;
  /** Đích của nhãn: bấm vào là đi thẳng tới đây. */
  to: string;
  /** Đường dẫn được coi là "đang ở mục này" — dùng để tô sáng. */
  activePaths: string[];
};

/**
 * Public destinations only. Anything behind a login lives in the dashboard and
 * is reached through the auth control, not from here — a visitor who clicks a
 * nav link and lands on a login screen has hit a dead end.
 *
 * Thứ tự do trung tâm chốt: Trang chủ · Bảng tin · Ôn tập & thi thử · Công cụ.
 */
const NAV_ITEMS: NavItem[] = [
  {
    // Trang chủ vốn đã cuộn qua đủ lịch thi · hạng bằng · giáo viên.
    key: 'trang-chu',
    label: 'Trang chủ',
    to: '/',
    activePaths: ['/'],
  },
  {
    // Bấm "Bảng tin" là muốn đọc tin ngay chứ không phải chọn chuyên mục trước,
    // mà trang /tin-tuc vốn đã có bộ lọc chuyên mục ngay đầu trang.
    key: 'bang-tin',
    label: 'Bảng tin',
    to: '/tin-tuc',
    activePaths: ['/tin-tuc'],
  },
  {
    // Trang /on-thi vốn đã bày đúng ba thẻ Ôn tập · Thi thử · Ôn mô phỏng.
    // `activePaths` giữ nguyên để nhãn vẫn sáng khi đang đứng trong trang con.
    key: 'on-thi',
    label: 'Ôn tập & thi thử',
    to: '/on-thi',
    activePaths: ['/on-thi', '/review', '/teststudent', '/on-mo-phong'],
  },
  {
    // Trang /tools là lưới đầy đủ có tên và mô tả từng công cụ, hơn hẳn một
    // danh sách nhồi trong tấm thả xuống — nên bấm nhãn là sang thẳng đó.
    key: 'cong-cu',
    label: 'Công cụ',
    to: '/tools',
    activePaths: ['/tools'],
  },
];

const isActivePath = (item: NavItem, pathname: string): boolean =>
  item.activePaths.some((path) => (path === '/' ? pathname === '/' : pathname.startsWith(path)));

export const Header: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, role, displayName, avatarUrl, logout } = useAuth();

  const defaultAvatar = getDefaultAvatar(role);
  const resolvedAvatar = avatarUrl || defaultAvatar;

  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const profileMenuRef = useRef<HTMLDivElement | null>(null);

  const handleLogout = useCallback(() => {
    logout();
    setIsProfileOpen(false);
    setIsMenuOpen(false);
    navigate('/');
  }, [logout, navigate]);

  // Route change closes both overlays — otherwise the drawer stays open on top
  // of the page the visitor just navigated to.
  useEffect(() => {
    setIsMenuOpen(false);
    setIsProfileOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!isProfileOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (profileMenuRef.current && !profileMenuRef.current.contains(event.target as Node)) {
        setIsProfileOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isProfileOpen]);

  useEffect(() => {
    if (!isMenuOpen && !isProfileOpen) return;

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      setIsMenuOpen(false);
      setIsProfileOpen(false);
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isMenuOpen, isProfileOpen]);

  /** Đi tới một đích của thanh điều hướng và đóng ngăn kéo đang mở. */
  const goTo = useCallback((to: string) => {
    setIsMenuOpen(false);
    navigate(to);
  }, [navigate]);

  const authControl = isAuthenticated ? (
    <div className="nb-header__profile" ref={profileMenuRef}>
      <button
        type="button"
        className="nb-header__chip"
        onClick={() => setIsProfileOpen((prev) => !prev)}
        aria-expanded={isProfileOpen}
      >
        <img
          src={resolvedAvatar}
          alt=""
          className="nb-header__chip-avatar"
          onError={(e) => {
            const target = e.currentTarget;
            if (target.src !== defaultAvatar) target.src = defaultAvatar;
          }}
        />
        <span className="nb-header__chip-name">{displayName || role || 'Tài khoản'}</span>
        <span className="nb-icon nb-icon--18" aria-hidden="true">
          {isProfileOpen ? 'expand_less' : 'expand_more'}
        </span>
      </button>

      {isProfileOpen && (
        <div className="nb-header__menu">
          <button
            type="button"
            className="nb-header__menu-item"
            onClick={() => navigate('/dashboard')}
          >
            <span className="nb-icon nb-icon--20" aria-hidden="true">dashboard</span>
            Trang quản lý
          </button>
          <div className="nb-header__menu-rule" />
          <button
            type="button"
            className="nb-header__menu-item nb-header__menu-item--danger"
            onClick={handleLogout}
          >
            <span className="nb-icon nb-icon--20" aria-hidden="true">logout</span>
            Đăng xuất
          </button>
        </div>
      )}
    </div>
  ) : (
    // Points at /dashboard, not /login: PrivateRoute bounces an anonymous
    // visitor to the login screen and back, so one click works for both states.
    <NavLink to="/dashboard" className="nb-btn nb-btn--primary nb-btn--pill nb-header__login">
      <span className="nb-icon nb-icon--18" aria-hidden="true">login</span>
      Đăng nhập
    </NavLink>
  );

  return (
    <header className="nb-header">
      <div className="nb-header__inner">
        <NavLink to="/" className="nb-header__brand" aria-label="Lái Xe Trung Bộ — trang chủ">
          <img src="/assets/images/logo.png" alt="" className="nb-header__logo" />
          <span className="nb-header__wordmark">LÁI XE TRUNG BỘ</span>
        </NavLink>

        <nav className="nb-header__nav" aria-label="Điều hướng chính">
          {NAV_ITEMS.map((item) => {
            const active = isActivePath(item, location.pathname);

            return (
              <Link
                key={item.key}
                to={item.to}
                className={`nb-header__link${active ? ' nb-header__link--active' : ''}`}
              >
                {item.label}
              </Link>
            );
          })}
        </nav>

        <div className="nb-header__actions">
          {/* [2026-08-27] TẠM ẨN số điện thoại khỏi header (cả bản desktop lẫn
              drawer mobile bên dưới). Số vẫn còn ở chân trang và ở nút gọi nổi
              góc phải, nên người cần liên hệ vẫn tìm được.
              Bật lại: bỏ ghi chú ở hai khối này. */}
          {authControl}

          <button
            type="button"
            className="nb-header__burger"
            onClick={() => setIsMenuOpen((prev) => !prev)}
            aria-expanded={isMenuOpen}
            aria-label={isMenuOpen ? 'Đóng menu' : 'Mở menu'}
          >
            <span className="nb-icon" aria-hidden="true">{isMenuOpen ? 'close' : 'menu'}</span>
          </button>
        </div>
      </div>

      {/* Ngăn kéo điện thoại: mỗi mục đúng một hàng, bấm là đi — không còn cấp
          con nào để gập/mở. Vẫn là <button> chứ không phải <a> vì đang đứng sẵn
          ở trang đó thì route không đổi, `goTo` mới tự đóng được ngăn kéo. */}
      {isMenuOpen && (
        <div className="nb-header__drawer">
          {NAV_ITEMS.map((item) => {
            const active = isActivePath(item, location.pathname);

            return (
              <button
                key={item.key}
                type="button"
                className={`nb-header__group${active ? ' nb-header__group--active' : ''}`}
                onClick={() => goTo(item.to)}
              >
                {item.label}
              </button>
            );
          })}
        </div>
      )}
    </header>
  );
};

export default Header;
